import {
  Button,
  Card,
  CardPreview,
  Dialog,
  DialogSurface,
  DialogTitle,
  Text,
  CardHeader,
  Divider,
  DialogContent,
  Link,
} from '@fluentui/react-components';
import { DismissFilled } from '@fluentui/react-icons';
import React from 'react';
import { IntlShape, useIntl } from 'react-intl';

import { Flex } from '@tdp/common/fluentV9MigrationComponents';

import { HomeLinks, HomePageAssets } from './constants';

interface ICopilotCTA {
  show: boolean;
  setShow: (val: boolean) => void;
}

export const getFormattedStrings = (intl: IntlShape) => ({
  copilotHomeCardTitle: intl.formatMessage({
    defaultMessage: 'New to Copilot agents?',
    description: 'Title for copilot Home Card',
  }),
  copilotHomeCardAgentTitle: intl.formatMessage({
    defaultMessage:
      "If you're new to Copilot, your first step is to figure out your development approach. Do you want to extend Microsoft Copilot 365 for targeted use cases? Or do want to build a bespoke agent?",
    description: 'Title for copilot agents',
  }),
  copilotHomeCardAgentSubtitle1: intl.formatMessage({
    defaultMessage: 'Extend Copilot',
    description: 'Title for extending copilots',
  }),
  copilotHomeCardAgentSubDesc1: intl.formatMessage({
    defaultMessage:
      'To customize Microsoft 365 Copilot with external data, tailored conversations, and custom prompts, build a declarative agent.',
    description: 'Description for extending copilots',
  }),
  copilotHomeCardAgentSubtitle2: intl.formatMessage({
    defaultMessage: 'Build your own agent',
    description: 'Title for building your own agent',
  }),
  copilotHomeCardAgentSubDesc2: intl.formatMessage({
    defaultMessage:
      'To use custom models, orchestrators, and logic, build a custom engine agent.',
    description: 'Description for building your own agent',
  }),
  declarativeAgentsLearnMore: intl.formatMessage({
    defaultMessage: 'Learn about declarative agents',
    description: 'Learn more for declarative agent',
  }),
  customEngineAgentsLearnMore: intl.formatMessage({
    defaultMessage: 'Learn about custom engine agents',
    description: 'Learn more about custom engine agent',
  }),
  copilotHomeCardToolkitTitle: intl.formatMessage({
    defaultMessage: 'Create agents using your tool of choice',
    description: 'Title for copilot vs-code tollkit',
  }),
  copilotHomeCardToolkitDescription: intl.formatMessage({
    defaultMessage:
      'For a code-first approach, use Microsoft 365 Agents Toolkit for Visual Studio Code.',
    description: 'Description for VS Code Extension',
  }),
  copilotHomeCardStudioDescription: intl.formatMessage({
    defaultMessage:
      'If you prefer a no-code or low-code tool, try Microsoft Copilot Studio.',
    description: 'Description forcopilot studio',
  }),
  copilotHomeCardAction2: intl.formatMessage({
    defaultMessage: 'Microsoft Copilot Studio',
    description: 'Action for build a declarative agent',
  }),
  copilotHomeCardAction3: intl.formatMessage({
    defaultMessage: 'Visual Studio',
    description: 'Action for get started',
  }),
});

export const CopilotCTA: React.FC<ICopilotCTA> = ({ show, setShow }) => {
  const intl = useIntl();
  const formattedStrings = getFormattedStrings(intl);
  return (
    <Dialog open={show}>
      <DialogSurface>
        <DialogContent>
          <Flex column fill gap="gap.medium">
            <Flex fill style={{ justifyContent: 'space-between' }}>
              <DialogTitle>{formattedStrings.copilotHomeCardTitle}</DialogTitle>
              <Button
                onClick={() => {
                  setShow(false);
                }}
                icon={<DismissFilled />}
                appearance="transparent"
              />
            </Flex>
            <Flex column>
              <Text>{formattedStrings.copilotHomeCardAgentTitle}</Text>
              <p>
                <b>{`${formattedStrings.copilotHomeCardAgentSubtitle1}: `}</b>
                {formattedStrings.copilotHomeCardAgentSubDesc1}&nbsp;
                <Link href={HomeLinks.CopilotDeclarativeAgents} target="_blank">
                  {formattedStrings.declarativeAgentsLearnMore}
                </Link>
              </p>
              <p style={{ marginTop: '12px' }}>
                {' '}
                <b>{`${formattedStrings.copilotHomeCardAgentSubtitle2}: `}</b>
                {formattedStrings.copilotHomeCardAgentSubDesc2}&nbsp;
                <Link href={HomeLinks.CopilotCustomEngine} target="_blank">
                  {formattedStrings.customEngineAgentsLearnMore}
                </Link>
              </p>
            </Flex>
            <Flex>
              <Divider />
            </Flex>
            <Flex column>
              <Text>{formattedStrings.copilotHomeCardToolkitTitle}</Text>
              <ul>
                <li>{formattedStrings.copilotHomeCardToolkitDescription}</li>
                <li>{formattedStrings.copilotHomeCardStudioDescription}</li>
              </ul>
            </Flex>
            <Flex gap="gap.medium">
              <ActionCard
                header={formattedStrings.copilotHomeCardAction3}
                src={HomePageAssets.vsCode}
                href={HomeLinks.InstallVSCodeTeamsToolkit}
                id={'HomePageCopilotVSCode'}
              />
              <ActionCard
                header={formattedStrings.copilotHomeCardAction2}
                src={HomePageAssets.copilotStudio}
                href={HomeLinks.CopilotStudio}
                id={'HomePageCopilotMCS'}
              />
            </Flex>
          </Flex>
        </DialogContent>
      </DialogSurface>
    </Dialog>
  );
};

const ActionCard: React.FC<{
  header: string;
  src: string;
  href: string;
  id: string;
}> = ({ header, src, href }) => {
  return (
    <Card
      orientation="horizontal"
      style={{ minWidth: '250px' }}
      onClick={() => {
        window.open(href, '_blank');
      }}
    >
      <CardPreview
        style={{
          background: '#f5f5f5',
          width: '48px',
          height: '48px',
          margin: '6px',
          borderRadius: '4px',
        }}
      >
        <img style={{ padding: '4px', height: "40px", width: "40px" }} src={src} />
      </CardPreview>
      <CardHeader header={<b>{header}</b>} />
    </Card>
  );
};
